import React, { useState, useEffect, useRef } from "react";
import { X, MessageSquareText, Camera, Trash2 } from "lucide-react";
import { api, storage } from "../lib/api";
import { REVIEW_TAGS } from "../lib/reviewTags";
import { compressImage } from "../lib/imageCompress";
import EmailNicknameModal from "./EmailNicknameModal";

export default function ReviewDialog({ open, onClose, venue, onDone }) {
  const [userId, setUserId] = useState(storage.getUserId());
  const [nickname, setNickname] = useState(storage.getUserNickname());
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoErr, setPhotoErr] = useState("");
  const [tags, setTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [askAuth, setAskAuth] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const uid = storage.getUserId();
      const nk = storage.getUserNickname();
      setUserId(uid);
      setNickname(nk);
      setRating(5);
      setText("");
      setPhoto(null);
      setPhotoErr("");
      setTags([]);
      setErr("");
      if (!uid) setAskAuth(true);
    }
  }, [open]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoErr("");
    try {
      if (file.size > 8 * 1024 * 1024) {
        setPhotoErr("Foto muy pesada (máx 8MB original)");
        return;
      }
      const data = await compressImage(file, { maxW: 800, quality: 0.7 });
      setPhoto(data);
    } catch {
      setPhotoErr("No pudimos leer esa imagen");
    }
  };

  const toggleTag = (t) => {
    setTags((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : prev.length >= 4 ? prev : [...prev, t],
    );
  };

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || text.trim().length < 5) {
      setErr("La reseña debe tener al menos 5 caracteres");
      return;
    }
    if (!userId) {
      setAskAuth(true);
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await api.addReview(venue.id, {
        user_id: userId,
        user_handle: nickname || "anonimo",
        rating,
        text: text.trim(),
        photo_base64: photo,
        tags,
        device_id: storage.getDeviceId(),
      });
      onDone?.();
      onClose();
    } catch (ex) {
      const msg = ex?.response?.data?.detail || "No pudimos publicar tu reseña";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="dialog-legible fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="review-dialog"
      onClick={onClose}
    >
      <div
        className="bg-[#1A1A1A] border-2 border-[#FEBA01] w-full max-w-md shadow-brutal my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-[#333]">
          <div className="flex items-center gap-2 font-display text-xl uppercase">
            <MessageSquareText size={20} className="text-[#FEBA01]" /> Dejar reseña
          </div>
          <button
            onClick={onClose}
            className="text-[#A3A3A3] hover:text-[#FEBA01]"
            data-testid="review-close"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="text-sm text-[#A3A3A3]">
            Sobre <span className="text-[#FEBA01] font-bold">{venue.name}</span>
            {nickname && (
              <span className="ml-2">
                · firmas como{" "}
                <button
                  type="button"
                  onClick={() => setAskAuth(true)}
                  className="text-[#FEBA01] underline decoration-dotted hover:text-white"
                  data-testid="review-edit-handle"
                >
                  {nickname}
                </button>
              </span>
            )}
          </div>

          <div>
            <label className="block font-body text-xs uppercase tracking-wider font-bold text-[#A3A3A3] mb-1">
              Rating
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  data-testid={`review-star-${n}`}
                  className={`flex-1 py-2 border-2 font-display text-xl ${
                    n <= rating
                      ? "bg-[#FEBA01] text-black border-[#FEBA01]"
                      : "bg-[#0A0A0A] text-[#333] border-[#333]"
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-body text-xs uppercase tracking-wider font-bold text-[#A3A3A3] mb-1">
              Reseña
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
              rows={4}
              maxLength={500}
              placeholder="Cuéntanos qué te pareció el local…"
              className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5]"
              data-testid="review-text-input"
            />
            <div className="text-right font-body text-xs text-[#A3A3A3] mt-1">{text.length}/500</div>
          </div>

          <div>
            <label className="block font-body text-xs uppercase tracking-wider font-bold text-[#A3A3A3] mb-2">
              Tags rápidos {tags.length > 0 && `· ${tags.length}/4`}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {REVIEW_TAGS.map((t) => {
                const active = tags.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    data-testid={`review-tag-${t.replace(/\s+/g, "-")}`}
                    className={`px-2.5 py-1 border-2 font-body text-[11px] uppercase tracking-wider font-semibold transition ${
                      active
                        ? "bg-[#FEBA01] text-black border-[#FEBA01]"
                        : "bg-[#0A0A0A] text-[#A3A3A3] border-[#333] hover:border-[#FEBA01] hover:text-[#FEBA01]"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block font-body text-xs uppercase tracking-wider font-bold text-[#A3A3A3] mb-1">
              Foto (opcional)
            </label>
            {!photo ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-[#333] hover:border-[#FEBA01] hover:text-[#FEBA01] text-[#A3A3A3] font-body text-sm font-semibold uppercase tracking-wider py-4 flex items-center justify-center gap-2 transition"
                data-testid="review-photo-btn"
              >
                <Camera size={16} /> Subir foto
              </button>
            ) : (
              <div className="relative">
                <img src={photo} alt="preview" className="w-full max-h-56 object-cover border-2 border-[#FEBA01]" />
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="absolute top-2 right-2 bg-black/80 text-[#FEBA01] border border-[#FEBA01] p-1.5 hover:bg-[#FEBA01] hover:text-black"
                  data-testid="review-photo-remove"
                  aria-label="Quitar foto"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handlePhoto}
              className="hidden"
              data-testid="review-photo-input"
            />
            {photoErr && <div className="mt-1 text-xs text-red-400">{photoErr}</div>}
          </div>

          {err && (
            <div className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-2" data-testid="review-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-brutal w-full bg-[#FEBA01] text-black font-display text-lg uppercase py-3 shadow-brutal-white disabled:opacity-50"
            data-testid="review-submit"
          >
            {submitting ? "Publicando…" : "Publicar reseña"}
          </button>
        </form>
      </div>

      <EmailNicknameModal
        open={askAuth}
        onClose={() => setAskAuth(false)}
        onDone={({ user_id, nickname: nk }) => { setUserId(user_id); setNickname(nk); }}
        title="Únete a la camada"
      />
    </div>
  );
}
